/**
 * Utilidades para manejo de JWT en el cliente
 * Nota: Solo decodificamos el token, no verificamos la firma (eso lo hace el backend)
 */

export interface JWTPayload {
  userId: number;
  userType: number;
  iat: number;
  exp: number;
}

/**
 * Decodifica un JWT sin verificar la firma
 * Útil para leer la información del token en el cliente
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    // Decodificar base64url
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decodificando JWT:', error);
    return null;
  }
}

/**
 * Verifica si un token JWT ha expirado
 * Añade un margen de seguridad de 30 segundos para evitar problemas de timing
 */
export function isTokenExpired(token: string, marginSeconds: number = 30): boolean {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return true;
  }
  
  // exp está en segundos, Date.now() está en milisegundos
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const marginMs = marginSeconds * 1000;
  
  return currentTime >= (expirationTime - marginMs);
}

/**
 * Obtiene el tiempo restante hasta que expire el token en milisegundos
 * Retorna 0 si el token ya expiró o es inválido
 */
export function getTokenTimeRemaining(token: string): number {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return 0;
  }
  
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const remaining = expirationTime - currentTime;
  
  return remaining > 0 ? remaining : 0;
}

/**
 * Formatea el tiempo restante en un formato legible (ej: "5 minutos")
 */
export function formatTimeRemaining(token: string): string {
  const remaining = getTokenTimeRemaining(token);
  
  if (remaining <= 0) {
    return 'Expirado';
  }
  
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  
  if (minutes > 0) {
    return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
  }
  
  return `${seconds} segundo${seconds !== 1 ? 's' : ''}`;
}

/**
 * Verifica si el token está próximo a expirar (menos de 5 minutos)
 */
export function isTokenExpiringSoon(token: string, thresholdMinutes: number = 5): boolean {
  const remaining = getTokenTimeRemaining(token);
  const thresholdMs = thresholdMinutes * 60 * 1000;
  
  return remaining > 0 && remaining <= thresholdMs;
}

